import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';

import CommonRequestTrialLinkComponent from 'webapp/components/common/request-trial-link';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CommonUpgradePlanLinkComponent from 'webapp/components/common/upgrade-plan-link';
import FormCheckboxSwitchComponent from 'webapp/components/form/checkbox-switch';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import type Block from 'webapp/models-custom/block';
import type Promotion from 'webapp/models-custom/promotion';

import type { TrackingOriginTypeForTrialPopupOpened } from 'webapp/services/billing';

interface Args {
	blockM: Block;
}

export default class BlockSettingsMediaCreativesComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;
	@service('account') declare accountService: Service.Account;
	@service('flow/promotions') declare flowPromotionsService: Service.Flow.Promotions;

	billingFeature = BillingFeature.MEDIA_CREATIVES;
	trackingOrigin: TrackingOriginTypeForTrialPopupOpened = 'designer_media_creatives';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// disable media creatives for users on trial or free that had media
		// creatives enabled before the feature was restricted
		if (!this.canUseMediaCreatives && this.block.mediaEnabled) {
			this.changeMediaEnabled(false);
		}
	}

	get block(): Block {
		return this.args.blockM;
	}

	get mediaEnabled(): boolean {
		return !!this.block.mediaEnabled;
	}

	get tooltipText(): string {
		const hideDataboxContent =
			this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins;
		let text = 'Display your ad creatives alongside related ads data.';
		if (!hideDataboxContent) {
			text +=
				' <a href="https://help.databox.com/enable-media-creatives-previews">Learn more</a>';
		}
		return text;
	}

	get availableTrialPromotion(): Promotion | undefined {
		const planForFeature = this.billingService.getPlanForTrial(this.billingFeature);
		return planForFeature
			? this.flowPromotionsService.getAvailableTrialPromotion(planForFeature)
			: undefined;
	}

	get canRequestTrial() {
		return this.billingService.canRequestTrial || !!this.availableTrialPromotion;
	}

	get canUseMediaCreatives(): boolean {
		return this.billingService.checkIfCan(BillingFeature.MEDIA_CREATIVES);
	}

	get showWarning(): boolean {
		return !this.canUseMediaCreatives;
	}

	@action
	changeMediaEnabled(value: boolean): void {
		const block = this.block;
		block.mediaEnabled = value;
		block.blockSetup.saveBlock();
	}
	<template>
		<div class="row row--media-creatives">
			<label>
				Media creatives
				<ToolTipWithIconComponent
					@text={{this.tooltipText}}
					@iconName="Question"
					@maxWidth={{230}}
				/>
				<div class="new-feature-tag">New</div>
			</label>

			{{#if this.showWarning}}
				<div class="warning">
					<CommonUiIconComponent @name="Lock" @size="sm" />

					<p>
						This option is not available with your current plan.

						{{#if this.canRequestTrial}}
							<CommonRequestTrialLinkComponent
								@feature={{this.billingFeature}}
								@trackingOrigin={{this.trackingOrigin}}
							/>
						{{else}}
							<CommonUpgradePlanLinkComponent
								@feature={{this.billingFeature}}
								@trackingOrigin={{this.trackingOrigin}}
							/>
						{{/if}}

					</p>
				</div>
			{{else}}
				<div class="media-switch">
					<FormCheckboxSwitchComponent
						@name="showMedia"
						@selected={{this.mediaEnabled}}
						@onChange={{this.changeMediaEnabled}}
					/>
					<label>Show thumbnails</label>
				</div>
			{{/if}}
		</div>
	</template>
}
